import type { TopLevelSpec } from 'vega-lite'

import type { MatrixResult, TerritoryPoint } from './api'

/**
 * Encoding da matriz, como especificação Vega-Lite.
 *
 * Função pura sobre o `MatrixResult` — é o pedaço da tela que carrega decisão de
 * produto, então é o pedaço que tem teste. Quem renderiza (`MatrixChart`) só passa
 * a spec adiante e escuta o clique.
 *
 * Os quatro encodings vêm do contrato e não são negociáveis aqui (ver o cabeçalho
 * de `contracts/matrix.py`): X afinidade, Y afinidade competitiva, tamanho alcance,
 * cor ocupação. O tamanho por alcance é o que impede a tela de vender um índice 300
 * sobre 1% da audiência.
 */

/** Extremos da escala divergente da tela: minha marca no petróleo, concorrente no
 * carmim, disputada no meio quente e branca no cinza-sálvia neutro. */
export const OCCUPANCY_COLORS: Record<string, string> = {
  minha: '#2a7b78',
  disputada: '#f48475',
  concorrente: '#a8141f',
  branca: '#c3d2cb',
}

export const OCCUPANCY_LABELS: Record<string, string> = {
  minha: 'Minha marca',
  disputada: 'Disputada',
  concorrente: 'Concorrente',
  branca: 'Branca',
}

export interface MatrixRow {
  territory_id: string
  label: string
  pillar: string
  affinity_index: number
  competitive_saturation: number
  reach_pct: number
  brand_affinity_index: number
  brand_occupancy: string
  quadrant: string
  /** Eixo Y apoiado em base que não sustenta leitura. Espelha
   * `TerritoryPoint.saturation_uncertain` do backend, que é propriedade derivada e
   * por isso não viaja no JSON. */
  saturation_uncertain: boolean
  occupancy_uncertain: boolean
  saturation_driver: string | null
  saturation_cell: number | null
  unweighted_base: number
  cell_unweighted: number | null
  significant: boolean | null
}

function uncertain(evidence: TerritoryPoint['saturation_evidence']): boolean {
  // Evidência ausente conta como incerta — é o caso da marca sem cobertura na
  // audiência, que não é "tudo bem", é "não medido". Mesma regra do backend.
  if (!evidence) return true
  return evidence.suppressed || evidence.low_base
}

export function toRows(result: MatrixResult): MatrixRow[] {
  return result.points.map((point) => {
    const driver = point.competitors.reduce<TerritoryPoint['competitors'][number] | null>(
      (best, candidate) => (best === null || candidate.index > best.index ? candidate : best),
      null,
    )
    return {
      territory_id: point.territory_id,
      label: point.label,
      pillar: point.pillar,
      affinity_index: point.affinity_index,
      competitive_saturation: point.competitive_saturation,
      reach_pct: point.reach_pct,
      brand_affinity_index: point.brand_affinity_index,
      brand_occupancy: point.brand_occupancy,
      quadrant: point.quadrant,
      saturation_uncertain: uncertain(point.saturation_evidence),
      occupancy_uncertain: uncertain(point.brand_evidence),
      saturation_driver: driver?.brand_id ?? null,
      saturation_cell: driver?.evidence.cell_unweighted ?? null,
      unweighted_base: point.evidence.unweighted_base,
      cell_unweighted: point.evidence.cell_unweighted,
      significant: point.evidence.significant,
    }
  })
}

export const POINT_SELECTION = 'territorio'

/**
 * Espelho de `MatrixResult.top()` no backend: o descarte fica no gráfico mas fora do
 * ranking. O score de awareness é 80% alcance e espaço livre, então um território que
 * a audiência sub-indexa pode liderar só por ser grande e sem concorrente — foi o HPV
 * em 1º na Bauducco, com afinidade 84. `??` e não `||`: permissão 0.0 é decisão, não
 * ausência.
 */
export function topPoints(points: TerritoryPoint[], n: number): TerritoryPoint[] {
  const rank = (p: TerritoryPoint) => p.adjusted_priority_score ?? p.priority_score
  return points
    .filter((p) => p.quadrant !== 'descarte')
    .sort((a, b) => rank(b) - rank(a))
    .slice(0, n)
}

export function buildMatrixSpec(result: MatrixResult, height = 560): TopLevelSpec {
  const rows = toRows(result)
  const { affinity_threshold, saturation_threshold } = result.brief.config

  return {
    $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
    background: 'transparent',
    autosize: { type: 'fit', contains: 'padding' },
    width: 'container',
    height,
    data: { values: rows as unknown as Record<string, unknown>[] },
    layer: [
      // As duas réguas primeiro: são o fundo que divide os quadrantes, e desenhar
      // depois das bolhas cortaria as bolhas ao meio.
      {
        mark: { type: 'rule', color: '#8fa8a2', strokeDash: [3, 4] },
        encoding: { x: { datum: affinity_threshold } },
      },
      {
        mark: { type: 'rule', color: '#8fa8a2', strokeDash: [3, 4] },
        encoding: { y: { datum: saturation_threshold } },
      },
      {
        params: [
          // `point` e não `interval`: o clique abre o painel lateral, e arrastar
          // para selecionar região não tem ação associada nesta tela.
          { name: POINT_SELECTION, select: { type: 'point', on: 'click', clear: 'dblclick' } },
        ],
        mark: { type: 'circle', cursor: 'pointer' },
        encoding: {
          x: {
            field: 'affinity_index',
            type: 'quantitative',
            title: 'Afinidade (índice vs. população)',
            scale: { zero: false, nice: true, padding: 24 },
            axis: { grid: true, gridColor: '#dde7e2', tickColor: '#b9c9c4' },
          },
          y: {
            field: 'competitive_saturation',
            type: 'quantitative',
            title: 'Afinidade competitiva (índice vs. audiência)',
            scale: { zero: false, nice: true, padding: 24 },
            axis: { grid: true, gridColor: '#dde7e2', tickColor: '#b9c9c4' },
          },
          size: {
            field: 'reach_pct',
            type: 'quantitative',
            title: 'Alcance na audiência (%)',
            scale: { range: [80, 2200] },
            legend: { format: '.1f' },
          },
          color: {
            field: 'brand_occupancy',
            type: 'nominal',
            title: 'Ocupação',
            scale: {
              domain: Object.keys(OCCUPANCY_COLORS),
              range: Object.values(OCCUPANCY_COLORS),
            },
            legend: { labelExpr: labelExpr(OCCUPANCY_LABELS) },
          },
          // Fragilidade nos dois eixos que saem da bateria de marca, marcada no
          // próprio ponto e não só no painel: quem olha o panorama precisa ver
          // que parte dos "campos de batalha" se apoia em poucos respondentes.
          //
          // Contorno tracejado = eixo Y incerto. Preenchimento esmaecido = cor
          // incerta. Hachura seria mais legível que opacidade, mas Vega-Lite não
          // tem preenchimento por padrão — exigiria descer para Vega e injetar um
          // `pattern` no SVG, o que trocaria a spec declarativa inteira por causa
          // de um encoding.
          strokeDash: {
            field: 'saturation_uncertain',
            type: 'nominal',
            title: 'Eixo Y',
            scale: { domain: [false, true], range: [[1, 0], [5, 3]] },
            legend: {
              labelExpr: "datum.label == 'true' ? 'base fina' : 'base suficiente'",
            },
          },
          strokeWidth: {
            condition: { test: 'datum.saturation_uncertain', value: 2 },
            value: 0.6,
          },
          stroke: { value: '#053b3e' },
          fillOpacity: {
            condition: { test: 'datum.occupancy_uncertain', value: 0.18 },
            value: 0.72,
          },
          tooltip: [
            { field: 'label', type: 'nominal', title: 'Território' },
            { field: 'pillar', type: 'nominal', title: 'Pilar' },
            { field: 'affinity_index', type: 'quantitative', title: 'Afinidade', format: '.0f' },
            {
              field: 'competitive_saturation',
              type: 'quantitative',
              title: 'Afinidade competitiva',
              format: '.0f',
            },
            { field: 'reach_pct', type: 'quantitative', title: 'Alcance %', format: '.1f' },
            { field: 'cell_unweighted', type: 'quantitative', title: 'Respondentes' },
          ],
        },
      },
    ],
    config: {
      axis: {
        labelFont: 'DM Mono, ui-monospace',
        labelFontSize: 10,
        labelColor: '#5e7573',
        titleFont: 'DM Sans, system-ui',
        titleFontSize: 12,
        titleFontWeight: 500,
        titleColor: '#24504f',
        domainColor: '#8fa8a2',
      },
      legend: {
        labelFont: 'DM Sans, system-ui',
        labelFontSize: 10,
        titleFont: 'DM Sans, system-ui',
        titleFontSize: 10,
        titleFontWeight: 600,
        orient: 'bottom',
        direction: 'horizontal',
        columns: 4,
        offset: 18,
      },
      view: { stroke: 'transparent' },
    },
  } as TopLevelSpec
}

/** Vega-Lite recebe rótulo por expressão, não por mapa. */
function labelExpr(labels: Record<string, string>): string {
  const clauses = Object.entries(labels).map(
    ([value, label]) => `datum.label == '${value}' ? '${label}'`,
  )
  return `${clauses.join(' : ')} : datum.label`
}
